import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Divider, Grid, MenuItem, Paper, Select, Stack, Typography } from "@mui/material";
import { onSnapshot, query } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GetAllGroupsInClass, GetClassRef, GetTasksInGroup } from "../FirebaseQueries";
import { GetScenario, Scenario, scenarioTitles } from "../Scenarios/Scenarios";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { green, grey, indigo, red } from "@mui/material/colors";
import { AnswersGroup, TaskDefinition } from "../Types";
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowDropDownIcon } from "@mui/x-date-pickers";

interface ClassData {
    code: string;
    session: number;
    scenarioIndex: number;
}

interface Task {
    id: string,
    index: number;
    name: string;
    answers: Map<string, Map<string, AnswersGroup[]>>;
    playersTotal: number;
    playersCorrect: number;
}

export default function ClassOverview() {
    let params = useParams();
    const [scenario, setScenario] = useState<Scenario>();
    const [classData, setClassData] = useState<ClassData>({
        code: "...",
        session: 1,
        scenarioIndex: 0,
    });
    const [groups, setGroups] = useState<Map<string, number>>(new Map());
    const [tasks, setTasks] = useState<Task[]>([]);
    const [wasTaskCorrectnessCalculated, setWasTaskCorrectnessCalculated] = useState<boolean>(false);
    const [displayTask, setDisplayTask] = useState<TaskDefinition | undefined>(undefined);

    const sortingOptions = {
        DEFAULT: 0,
        MOST_CORRECT: 1,
        LEAST_CORRECT: 2
    }
    const [sorting, setSorting] = useState<number>(sortingOptions.DEFAULT);


    const classId = (): string => {
        return params.classId !== undefined ? params.classId : "";
    };

    // Get Class
    useEffect(() => {
        const unsubscribe = onSnapshot(GetClassRef(classId()), (snapshot) => {
            if (snapshot.exists()) {
                setClassData({
                    code: snapshot.data().code,
                    session: snapshot.data().session,
                    scenarioIndex: snapshot.data().scenarioIndex,
                });
                setScenario(GetScenario(snapshot.data().scenarioIndex));
            } else {
            }
        });
        return unsubscribe;
    }, []);

    // Populate tasks
    useEffect(() => {
        if (scenario === undefined) return;
        let temp: Task[] = []
        for (const taskDef of scenario.tasks) {
            temp.push({
                id: taskDef.id,
                index: taskDef.taskIndex,
                name: taskDef.name,
                answers: new Map(),
                playersTotal: 0,
                playersCorrect: 0
            })
        }
        setTasks(temp);
    }, [scenario]);

    // Get Groups
    useEffect(() => {
        const classId = params.classId !== undefined ? params.classId : "";
        const q = GetAllGroupsInClass(classId);
        const unsubscribe = onSnapshot(q, (snapshot) => {
            if (!snapshot.empty) {
                let temp: Map<string, number> = new Map();
                snapshot.forEach((doc) => temp.set(doc.id, doc.data().groupNumber));
                setGroups(temp);
            } else {
                // Do something if empty
            }
        });
        return unsubscribe;
    }, []);

    // Get Tasks
    useEffect(() => {
        if (groups.size === 0) return;

        groups.forEach((_value, key) => {
            const q = query(GetTasksInGroup(key));
            onSnapshot(q, (tasksSnapshot) => {
                if (!tasksSnapshot.empty) {
                    tasksSnapshot.forEach((doc) => {
                        setTasks((prev) => {
                            let itemIndex = prev.findIndex((item) => item.index === doc.data().taskIndex);
                            if (itemIndex !== -1) prev[itemIndex].answers.set(key, docAnswersToMap(doc.data().answers));
                            return [...prev];
                        });
                    });
                } else {
                    // if empty, place blank
                    if (scenario !== undefined) {
                        for (const task of scenario.tasks) {
                            setTasks((prev) => {
                                let itemIndex = prev.findIndex((item) => item.index === task.taskIndex);
                                if (itemIndex !== -1) prev[itemIndex].answers.set(key, new Map());
                                return [...prev];
                            });
                        }
                    }
                }
            });
        });
    }, [groups]);

    // Calculate correctness
    useEffect(() => {
        if (tasks.length === 0 || wasTaskCorrectnessCalculated) return;

        let allSet: boolean = true;

        for (const task of tasks) {
            if (task.answers.size !== groups.size) {
                allSet = false;
                break;
            }
        }

        if (allSet) {
            let tempTasks = tasks;
            for (let i = 0; i < tasks.length; i++) {
                const taskDef = scenario?.tasks.find((value) => value.taskIndex === tasks[i].index);
                if (taskDef === undefined) continue;

                let totalCorrectInTask = 0;
                let totalInTask = 0;

                tasks[i].answers.forEach((group) => {
                    group.forEach((answer) => {
                        if (areAnswersSame(taskDef.correctAnswersGroups, answer)) {
                            totalCorrectInTask++;
                        }
                        totalInTask++;
                    })
                });

                tempTasks[i].playersCorrect = totalCorrectInTask;
                tempTasks[i].playersTotal = totalInTask;
            }

            setWasTaskCorrectnessCalculated(true);
            setTasks([...tempTasks]);
        }

    }, [tasks]);

    const docAnswersToMap = (obj: object): Map<string, AnswersGroup[]> => {
        let map = new Map();
        for (const [key, value] of Object.entries(obj)) map.set(key, value);
        return map;
    }

    const percentOfGroupCorrectInTask = (index: number, playerAnswers: Map<string, AnswersGroup[]>): number => {
        if (scenario === undefined || playerAnswers === null) return 0;
        const taskDef = scenario.tasks.find((task) => task.taskIndex === index);
        if (taskDef === undefined) return 0;

        let playersCorrectCount = 0;

        playerAnswers.forEach((answersGroup) => {
            playersCorrectCount = areAnswersSame(taskDef?.correctAnswersGroups, answersGroup) ? playersCorrectCount + 1 : playersCorrectCount;
        });

        return playersCorrectCount === 0 ? 0 : Math.round((playersCorrectCount / playerAnswers.size) * 100);
    }

    const areAnswersSame = (answersA: AnswersGroup[], answersB: AnswersGroup[]): boolean => {
        if (answersA.length !== answersB.length) return false;

        for (let i = 0; i < answersA.length; i++) {
            if (answersA[i].indexes.length !== answersB[i].indexes.length) return false;

            for (const answerIndexA of answersA[i].indexes) {
                if (!answersB[i].indexes.some((value) => value === answerIndexA)) return false;
            }
        }

        return true;
    }

    const getColorFromPercent = (percent: number) => {
        if (percent >= 75) {
            return "success";
        } else if (percent >= 50) {
            return "warning";
        } else {
            return "error";
        }
    }

    const getMostCorrectQuestion = (): string => {
        if (tasks.length === 0) return "";

        let highestValue = -1;
        let highestTask = "";

        for (let i = 0; i < tasks.length; i++) {
            let checkValue = tasks[i].playersCorrect === 0 ? 0 : (tasks[i].playersCorrect / tasks[i].playersTotal);
            if (checkValue > highestValue) {
                highestValue = checkValue;
                highestTask = tasks[i].name;
            }
        }
        return highestTask;
    }

    const getLeastCorrectQuestion = (): string => {
        if (tasks.length === 0) return "";

        let lowestValue = 101;
        let loswetTask = "";

        for (let i = 0; i < tasks.length; i++) {
            let checkValue = tasks[i].playersCorrect === 0 ? 0 : (tasks[i].playersCorrect / tasks[i].playersTotal);
            if (checkValue < lowestValue) {
                lowestValue = checkValue;
                loswetTask = tasks[i].name;
            }
        }
        return loswetTask;
    }

    function showTaskDetails(index: number) {
        setDisplayTask(scenario?.tasks.find((value) => value.taskIndex === index));
    }

    function AnswersGroupsToText(answers: AnswersGroup[]): string {
        var value: string = "";

        answers.forEach((answerGroup: AnswersGroup) => {
            if (answers.length > 1) value += `${answerGroup.title}: `;
            value += `${IndexesToLetters(answerGroup.indexes)} `;
        });

        return value;
    }

    function IndexesToLetters(indexes: number[]): string {
        const sorted: number[] = indexes.sort();
        let value: string = "";

        sorted.forEach((i) => {
            if (i >= 0) {
                value += String.fromCharCode(i + 65);
            } else {
                value += "_";
            }
        });

        return value;
    }

    function handleSortingChange(event: any) {
        let value = event.target.value
        setSorting(value);

        if (value === sortingOptions.DEFAULT) {
            setTasks((prev) => {
                prev.sort((a, b) => (a.name.localeCompare(b.name)));
                return [...prev];
            });
        } else if (value === sortingOptions.MOST_CORRECT) {
            setTasks((prev) => {
                prev.sort((a, b) => (
                    (b.playersCorrect === 0 ? 0 : b.playersCorrect / b.playersTotal) - (a.playersCorrect === 0 ? 0 : a.playersCorrect / a.playersTotal)
                ));
                return [...prev];
            });
        } else if (value === sortingOptions.LEAST_CORRECT) {
            setTasks((prev) => {
                prev.sort((a, b) => (
                    (a.playersCorrect === 0 ? 0 : a.playersCorrect / a.playersTotal) - (b.playersCorrect === 0 ? 0 : b.playersCorrect / b.playersTotal)
                ));
                return [...prev];
            });
        }
    }

    const getPercent = (a: number, b: number): number => {
        if (a === 0) return 0;
        return Math.round((a / b) * 100);
    }

    return (
        <Stack spacing={2}>
            {/* ---------- Top ---------- */}
            <Card>
                <Stack direction={"row"} spacing={2}>
                    <Box sx={{ width: "100%" }}>
                        <Typography variant="subtitle1" textAlign={"left"}>
                            Overview
                        </Typography>
                        <Divider />
                    </Box>
                    <Button href={`/app/classes/${classId()}`} variant="contained" sx={{ minWidth: "150px" }}>
                        Back to Class
                    </Button>
                </Stack>
                <Stack marginTop={1} alignItems={"flex-start"} spacing={0.5}>
                    <Stack direction={"row"} alignItems={"center"} spacing={4}>
                        <Typography fontWeight={"bold"}>Class: {classData.code}</Typography>
                        <Typography fontWeight={"bold"}>Session: {classData.session}</Typography>
                        <Typography fontWeight={"bold"}>Scenario: {scenarioTitles[classData.scenarioIndex]}</Typography>
                    </Stack>
                </Stack>
                <Stack marginTop={2} spacing={1}>
                    <Stack direction={"row"} spacing={1} justifyContent={"flex-start"} alignItems={"center"}>
                        <Stack
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            sx={{
                                backgroundColor: green[100],
                                borderRadius: 1,
                                padding: "0.25em 1.5em",
                            }}
                        >
                            <ArrowUpwardIcon sx={{ color: green[600] }}></ArrowUpwardIcon>
                        </Stack>
                        <Typography>Best Question: {getMostCorrectQuestion()}</Typography>
                    </Stack>
                    <Stack direction={"row"} spacing={1} justifyContent={"flex-start"} alignItems={"center"}>
                        <Stack
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                            sx={{
                                backgroundColor: red[100],
                                borderRadius: 1,
                                padding: "0.25em 1.5em",
                            }}
                        >
                            <ArrowDownwardIcon sx={{ color: red[600] }}></ArrowDownwardIcon>
                        </Stack>
                        <Typography>Worst Question: {getLeastCorrectQuestion()}</Typography>
                    </Stack>
                </Stack>
            </Card>
            {/* ---------- Main ---------- */}
            <Card>
                <Stack direction="row" justifyContent={"left"} alignItems={"center"} spacing={1} marginBottom={4}>
                    <Typography>Sorting:</Typography>
                    <Select
                        id="sorting-select"
                        value={sorting}
                        onChange={handleSortingChange}
                    >
                        <MenuItem value={sortingOptions.DEFAULT}>Default</MenuItem>
                        <MenuItem value={sortingOptions.MOST_CORRECT}>Most Correct</MenuItem>
                        <MenuItem value={sortingOptions.LEAST_CORRECT}>Least Correct</MenuItem>
                    </Select>
                </Stack>
                <Grid container spacing={2}>
                    {/* ---------- Tasks ---------- */}
                    <Grid size={2}>
                        <Typography marginBottom={4} variant="subtitle1">
                            Tasks
                        </Typography>
                        <Stack spacing={1} paddingTop={1}>
                            <Box height={40}></Box>
                            {tasks.map((task) => (
                                <Button
                                    key={task.id}
                                    variant="contained"
                                    onClick={() => showTaskDetails(task.index)}
                                    sx={{
                                        height: 50
                                    }}>
                                    {task.name.split(":")[0]}
                                </Button>
                            ))}
                        </Stack>
                    </Grid>
                    {/* ---------- Groups ---------- */}
                    <Grid size={5}>
                        <Typography marginBottom={4} variant="subtitle1">
                            Groups
                        </Typography>
                        <Stack sx={{ overflowX: "scroll", padding: 1, border: "1px solid", borderRadius: 1, borderColor: grey[300] }} spacing={1}>
                            <Stack height={40} direction={"row"} spacing={2}>
                                <Stack minWidth={50} justifyContent={"center"} alignItems={"center"}>
                                    <Typography variant="subtitle2">Total</Typography>
                                </Stack>
                                {Array.from(groups).sort((a, b) => a[1] - b[1]).map((group) => (
                                    <Stack key={group[0]} minWidth={50} justifyContent={"center"} alignItems={"center"}>
                                        <Typography variant="subtitle2">{group[1]}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            {tasks.map((task, index) => (
                                <Stack key={index} direction={"row"} height={50} spacing={2}>
                                    {/* ---------- Total Counts ---------- */}
                                    <Stack sx={{
                                        boxSizing: "border-box",
                                        position: 'relative',
                                        display: 'inline-flex',
                                        justifyContent: "center",
                                        alignContent: "center"
                                    }}>
                                        <CircularProgress
                                            enableTrackSlot
                                            size={50}
                                            variant="determinate"
                                            value={getPercent(task.playersCorrect, task.playersTotal)}
                                            color={getColorFromPercent(getPercent(task.playersCorrect, task.playersTotal))} />
                                        <Box
                                            sx={{
                                                top: 0,
                                                left: 0,
                                                bottom: 0,
                                                right: 0,
                                                position: 'absolute',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                component="div"
                                                sx={{ color: 'text.secondary' }}
                                            >{`${getPercent(task.playersCorrect, task.playersTotal)}%`}</Typography>
                                        </Box>
                                    </Stack>
                                    {/* ---------- Group Counts ---------- */}
                                    {Array.from(task.answers).sort((a, b) => {
                                        const numA = groups.get(a[0]);
                                        const numB = groups.get(b[0]);
                                        if (numA !== undefined && numB !== undefined) return numA - numB;
                                        return 0;
                                    }).map((group) => (
                                        <Stack key={group[0]} sx={{
                                            boxSizing: "border-box",
                                            position: 'relative',
                                            display: 'inline-flex',
                                            justifyContent: "center",
                                            alignContent: "center"
                                        }}>
                                            {group[1].size > 0 ?
                                                <>
                                                    <CircularProgress
                                                        enableTrackSlot
                                                        size={50}
                                                        variant="determinate"
                                                        value={percentOfGroupCorrectInTask(task.index, group[1])}
                                                        color={getColorFromPercent(percentOfGroupCorrectInTask(task.index, group[1]))} />
                                                    <Box
                                                        sx={{
                                                            top: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            component="div"
                                                            sx={{ color: 'text.secondary' }}
                                                        >{`${percentOfGroupCorrectInTask(task.index, group[1])}%`}</Typography>
                                                    </Box>
                                                </>
                                                :
                                                <Box
                                                    height={50}
                                                    width={50}
                                                    sx={{
                                                        borderRadius: 100,
                                                        backgroundColor: grey[500],
                                                        opacity: 0.2
                                                    }}>
                                                </Box>
                                            }
                                        </Stack>
                                    ))}
                                </Stack>
                            ))}
                        </Stack>
                    </Grid>
                    {/* ---------- Details ---------- */}
                    <Grid size={5}>
                        <Typography marginBottom={4} variant="subtitle1">
                            Details
                        </Typography>
                        <Paper
                            elevation={3}
                            sx={{ padding: 2 }}>
                            {displayTask === undefined ?
                                <></>
                                :
                                <>
                                    <Typography textAlign="left" variant="subtitle1" marginBottom={2}>{displayTask.name}</Typography>
                                    <Typography textAlign="left">{displayTask.question}</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <Box
                                            sx={{
                                                pb: 1,
                                                textAlign: "left",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: "bold",
                                                    backgroundColor: indigo[50],
                                                    color: indigo[900],
                                                    border: `1px solid var(--template-palette-divider)`,
                                                    borderRadius: 0.5,
                                                    py: 0.5,
                                                    px: 1,
                                                }}
                                            >
                                                {displayTask.correctAnswersGroups.length === 1 ? "Multiple Choice" : "Drag and Drop"}
                                            </Box>
                                            <Typography>Correct Answer(s): {AnswersGroupsToText(displayTask.correctAnswersGroups)}</Typography>
                                        </Box>
                                        <Box>
                                            {displayTask.answers.map((answer, index) => {
                                                return (
                                                    <Accordion key={index}>
                                                        <AccordionSummary
                                                            sx={{
                                                                pl: 1,
                                                                py: 0.5,
                                                                minHeight: 0,
                                                                fontSize: "0.875rem",
                                                            }}
                                                            expandIcon={<ArrowDropDownIcon />}
                                                        >
                                                            {"(" + String.fromCharCode(index + 65) + ")"} {answer.text}
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Typography sx={{ fontSize: "0.875rem", textAlign: "left" }}>{answer.feedback}</Typography>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </>
                            }
                        </Paper>
                    </Grid>
                </Grid>
            </Card>
        </Stack >
    );
}
